import { Card, Select } from '@radix-ui/themes'
import TextField from '../common/TextField'
import { Search, X, Loader2, Check, AlertCircle, Globe } from 'lucide-react'
import Snackbar from '../Snackbar'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import storageService from '@/services/wallet/storage.service'
import tokenValidationService from '@/services/blockchain/token-validation.service'
import { useWalletStore } from '@/store/walletStore'
import { getAllNetworks } from '@/config/networks'
import { getApiBaseUrl } from '@/config/app'

const TRUST_WALLET_CHAIN: Record<string, string> = {
  bsc: 'smartchain',
  ethereum: 'ethereum',
  polygon: 'polygon',
  arbitrum: 'arbitrum',
  optimism: 'optimism',
}

function getTrustWalletLogoUrl(networkId: string, address: string): string {
  const chain = TRUST_WALLET_CHAIN[networkId]
  if (!chain) return ''
  try {
    const checksumAddress = ethers.getAddress(address)
    return `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/${chain}/assets/${checksumAddress}/logo.png`
  } catch {
    return ''
  }
}

async function fetchCoinGeckoLogo(symbol: string): Promise<string> {
  try {
    const response = await fetch(`${getApiBaseUrl()}/api/proxy/coingecko/search?query=${encodeURIComponent(symbol)}`)
    if (!response.ok) return ''
    const data = await response.json()
    const match = (data.coins as Array<{ symbol: string; large: string; thumb: string }>)
      ?.find(c => c.symbol.toLowerCase() === symbol.toLowerCase())
    return match?.large || match?.thumb || ''
  } catch {
    return ''
  }
}

interface AddAssetDialogProps {
  open: boolean
  onClose: () => void
  onTokenAdded?: () => void
}

const EMPTY_FORM = { address: '', symbol: '', decimals: '', name: '' }

type TokenInfoStatus = 'idle' | 'loading' | 'success' | 'error' | 'duplicate'

const AddAssetDialog = ({ open, onClose, onTokenAdded }: AddAssetDialogProps) => {
  const { t } = useTranslation()
  const selectedNetwork = useWalletStore(state => state.selectedNetwork)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [network, setNetwork] = useState(selectedNetwork)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [tokenInfoStatus, setTokenInfoStatus] = useState<TokenInfoStatus>('idle')
  const availableNetworks = getAllNetworks().filter(
    (n) => n.id === 'omdb' || n.id === 'bsc' || n.isCustom
  )
  const [snackbarState, setSnackbarState] = useState<{
    isOpen: boolean
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    message: '',
    type: 'info'
  })

  useEffect(() => {
    if (open) {
      setNetwork(selectedNetwork)
    } else {
      setFormData(EMPTY_FORM)
      setTokenInfoStatus('idle')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Al escribir/pegar una dirección con formato válido, consulta el contrato en la red
  // seleccionada y autocompleta symbol/decimals/name (el usuario los puede seguir editando)
  useEffect(() => {
    setTokenInfoStatus('idle')

    if (!ethers.isAddress(formData.address)) return

    if (tokenValidationService.isAddressAlreadyInSystem(formData.address, network)) {
      setTokenInfoStatus('duplicate')
      return
    }

    let cancelled = false
    const timer = setTimeout(async () => {
      setTokenInfoStatus('loading')
      const result = await tokenValidationService.validateToken(formData.address, network)
      if (cancelled) return

      if (result.isValid && result.token) {
        setFormData((prev) => ({
          ...prev,
          symbol: result.token!.symbol ?? prev.symbol,
          decimals:
            result.token!.decimals !== undefined ? String(result.token!.decimals) : prev.decimals,
          name: result.token!.name ?? prev.name,
        }))
        setTokenInfoStatus('success')
      } else {
        setTokenInfoStatus('error')
      }
    }, 500)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.address, network])

  const handlePasteTrim =
    (field: keyof typeof formData) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      setFormData((prev) => ({ ...prev, [field]: e.clipboardData.getData('text').trim() }))
    }

  const isFormValid =
    tokenInfoStatus !== 'duplicate' &&
    ethers.isAddress(formData.address) &&
    formData.symbol.trim().length > 0 &&
    formData.decimals.trim() !== '' &&
    !isNaN(Number(formData.decimals)) &&
    Number(formData.decimals) >= 0 &&
    Number(formData.decimals) <= 36

  const handleSubmit: React.SubmitEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    if (isSubmitting || !isFormValid) return

    setIsSubmitting(true)

    // Intentar CoinGecko primero (agnóstico de red), luego Trust Wallet como fallback
    const logoUrl =
      (await fetchCoinGeckoLogo(formData.symbol)) ||
      getTrustWalletLogoUrl(network, formData.address)

    const saved = storageService.saveCustomToken({
      address: formData.address.trim(),
      symbol: formData.symbol.trim().toUpperCase(),
      decimals: parseInt(formData.decimals, 10),
      name: formData.name.trim(),
      network: network,
      isNative: false,
      logoUrl,
    })

    setIsSubmitting(false)

    if (saved) {
      setSnackbarState({ isOpen: true, message: t('dialogs.token_added_success'), type: 'success' })
      onTokenAdded?.()
      setTimeout(() => onClose(), 2000)
    } else {
      setSnackbarState({ isOpen: true, message: t('dialogs.token_already_exists'), type: 'error' })
    }
  }

  return (
    <div
      className="fixed inset-0 bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50"
      style={{ display: open ? 'block' : 'none' }}
    >
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <Card
          className={`shadow-xl bg-app dark:bg-card w-fit justify-center relative`}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-text-secondary z-10"
            aria-label="Close dialog"
            type="button"
          >
            <X className="w-6 h-6" />
          </button>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5.5 px-2 py-4 lg:p-4"
          >
            <div className="flex flex-col gap-1">
              <h2 className="text-text-main text-3xl font-light">
                {t('dialogs.add_asset_title')}
              </h2>

              <p className="text-text-secondary text-sm">
                {t('dialogs.add_asset_desc')}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs text-text-main font-light uppercase tracking-[2.4px]">
                {t('dialogs.token_network')}
              </label>
              <Select.Root
                value={network}
                onValueChange={(val) => {
                  setNetwork(val)
                  setFormData(EMPTY_FORM)
                  setTokenInfoStatus('idle')
                }}
              >
                <Select.Trigger radius="full" color="amber" variant="soft" />
                <Select.Content color="amber">
                  {availableNetworks.map((n) => (
                    <Select.Item key={n.id} value={n.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
                          {n.logoUrl ? (
                            <img
                              src={n.logoUrl}
                              alt={n.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Globe className="w-2.5 h-2.5 text-primary" />
                          )}
                        </div>
                        <span>{n.name}</span>
                      </div>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs text-text-main font-light uppercase tracking-[2.4px]">
                {t('dialogs.contract_address')}
              </label>
              <TextField
                icon={<Search className="w-4 h-4" />}
                placeholder={t('dialogs.contract_placeholder')}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                onPaste={handlePasteTrim('address')}
              />
              {tokenInfoStatus === 'loading' && (
                <p className="flex items-center gap-1.5 text-xs text-text-secondary">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {t('dialogs.fetching_token_info')}
                </p>
              )}
              {tokenInfoStatus === 'success' && (
                <p className="flex items-center gap-1.5 text-xs text-success">
                  <Check className="w-3.5 h-3.5" />
                  {t('dialogs.token_info_found')}
                </p>
              )}
              {tokenInfoStatus === 'error' && (
                <p className="flex items-center gap-1.5 text-xs text-text-secondary">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {t('dialogs.fill_manually')}
                </p>
              )}
              {tokenInfoStatus === 'duplicate' && (
                <p className="flex items-center gap-1.5 text-xs text-red-500">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {t('dialogs.token_already_in_system')}
                </p>
              )}
            </div>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs text-text-main font-light uppercase tracking-[2.4px]">
                  {t('dialogs.token_symbol')}
                </label>
                <TextField
                  value={formData.symbol}
                  onChange={(e) =>
                    setFormData({ ...formData, symbol: e.target.value })
                  }
                  onPaste={handlePasteTrim('symbol')}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs text-text-main font-light uppercase tracking-[2.4px]">
                  {t('dialogs.decimals')}
                </label>
                <TextField
                  type="number"
                  value={formData.decimals}
                  onChange={(e) =>
                    setFormData({ ...formData, decimals: e.target.value })
                  }
                  onPaste={handlePasteTrim('decimals')}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs text-text-main font-light uppercase tracking-[2.4px]">
                {t('dialogs.token_name')}
              </label>
              <TextField
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                onPaste={handlePasteTrim('name')}
              />
            </div>
            <button
              type="submit"
              className="btn-gradient-fill rounded-full py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting
                ? t('dialogs.adding_token')
                : t('dialogs.add_token')}
            </button>
          </form>
        </Card>
      </div>
      <Snackbar
        message={snackbarState.message}
        isOpen={snackbarState.isOpen}
        type={snackbarState.type}
        onClose={() => setSnackbarState((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default AddAssetDialog
